import React from 'react';

import Button from '@common/react/components/Forms/Button';
import VideoChatButton from '@common/react/components/UI/VideoChat/VideoChatButton';
import SystemCallButton from '@common/react/components/UI/VideoChat/SystemCallButton';
import AddUserToChatPopup from '@common/react/components/Chat/AddUserToChatPopup';
import { ChatKind } from '@common/react/objects/Chat';
import ChatNameEditor from '@common/react/components/Chat/ChatNameEditor';

export enum ChatMessagesHeaderComponents {
	VideoCall,
	AddUserToChat,
	LeaveChat,
	AudioCall,
	CompleteChat,
	Members,
	ChatNameEditor
}

export type ChatNameType = ({currentChat, user, withRemoteId, patientId}) => React.ReactNode;

type CustomComponent = (({currentChat, user, leaveChat, history, patientId, state, completeChat, withRemoteId, actions}) => React.ReactNode);

export type ChatMessagesHeaderSettingsType = Array<ChatMessagesHeaderComponents | CustomComponent>;

export const ChatMessagesHeaderDictionary = {
	[ChatMessagesHeaderComponents.VideoCall]: ({withRemoteId, currentChat, user, history, patientId}) => {
		const userCall = currentChat.contacts.find(item => withRemoteId ? item.remoteId !== user.id : item.id !== user.id);
		return currentChat.contacts.length <= 2 && <VideoChatButton
			roomCreateActor={withRemoteId ? 'videoChatSupport' : 'videoChat'}
			roomId={`${currentChat.id}_${currentChat.name}`}
			roomName={`${userCall?.firstName} ${userCall?.lastName}`}
			chatId={currentChat.id}
			notifyUserIds={withRemoteId ? [] : currentChat.contactsIds}
			avatar={userCall?.avatar}
			button={(onClick, isDisabled) =>
				<button
					className="btn btn-sm btn-primary pull-right"
					onClick={onClick}
					disabled={isDisabled}
					title={withRemoteId ? 'Video call to the patient' : 'Video Call'}
				>
					<i className="fa fa-video-camera"/>
				</button>}
		/>;
	},
	[ChatMessagesHeaderComponents.AudioCall]: ({withRemoteId, currentChat, user}) => {
		const userCall = currentChat.contacts.find(item => withRemoteId ? item.remoteId !== user.id : item.id !== user.id);
		return <SystemCallButton
			roomCreateActor={withRemoteId ? 'videoChatSupport' : 'videoChat'}
			roomId={`${currentChat.id}_${currentChat.name}`}
			roomName={`${userCall?.firstName} ${userCall?.lastName}`}
			chatId={currentChat.id}
			avatar={userCall?.avatar}
			notifyUserIds={withRemoteId ? [] : currentChat.contactsIds}
			button={(onClick, isDisabled) =>
				<button
					className="btn btn-sm btn-primary pull-right audio-call"
					onClick={onClick}
					disabled={isDisabled}
					title={withRemoteId ? 'Audio call to the patient' : 'Audio Call'}
				>
					<i className="fa fa-phone-square"/>
				</button>}
		/>;
	},
	[ChatMessagesHeaderComponents.AddUserToChat]: ({user, currentChat}) =>
		currentChat.userId === user?.id && currentChat.kind === ChatKind.Group &&
		<AddUserToChatPopup
			user={user}
			chat={currentChat}
			render={show =>
				<button
					className="btn btn-sm btn-primary pull-right"
					title="Add user to chat"
					onClick={show}
				>
					<i className="fa fa-plus"/>
				</button>
			}
			type="userChatViewList"
		/>,
	[ChatMessagesHeaderComponents.LeaveChat]: ({leaveChat, currentChat, withRemoteId, user}) =>
		currentChat.kind === ChatKind.Group
		&& (!withRemoteId || currentChat.contacts.some(item => item.remoteId === user.id))
		&& <Button
			onClick={leaveChat}
			className="btn btn-sm btn-danger pull-right"
			title="Leave the chat"
		>
			<i className="fa fa-times" />
		</Button>,
	[ChatMessagesHeaderComponents.CompleteChat]: ({state, currentChat, user, completeChat, withRemoteId}) =>
		currentChat.contacts.some(item => withRemoteId ? item.remoteId === user?.id : item.id === user?.id) && <Button
			className="btn btn-sm btn-primary pull-right is-relative"
			title={currentChat.archive ? 'Activate chat' : 'Complete chat'}
			isLoading={state.completeChatLoading}
			onClick={() => completeChat(currentChat)}
		>
			<i className="fa fa-archive" aria-hidden="true"/>
			{currentChat.archive && <i className="fa fa-times disabled-icon"/>}
		</Button>,
	[ChatMessagesHeaderComponents.Members]: ({currentChat}) =>
		<span className="chat-component__headline__count pull-left">
			<span className="chat-component__headline__count">
				{currentChat.contacts.length} members
			</span>
		</span>,
	[ChatMessagesHeaderComponents.ChatNameEditor]: ({currentChat, actions}) =>
		currentChat ? <ChatNameEditor
			render={show => <button
				type="button"
				title="Edit ChatName"
				className="btn btn-sm btn-primary pull-right"
				onClick={show}
			>
				<i className="fa fa-pencil"/>
			</button>}
			onSave={chat => actions.updateChat(chat)}
			chat={currentChat}
		/> : null
};
